import { ComponentObservable, componentObserver } from '../../../component-observer';
import { selectWishListOverlay } from '../../overlays/e-catalog-overlays/select-wish-list-overlay.module';
import { getAllWishlists, addItemToWishList, deleteItemFromWishList } from '../../../api/wish-list-api';
import { eCatalog } from '../../e-catalog/e-catalog.module';
import { toastNotification } from '../../toast-notification/toast-notification.module';
import { currentBreakpoint } from '../../../utils/resize.utils';
import { wishList } from '../../wish-list/wish-list.module';
import { wishListData } from '../../../wish-list-data.module';
import {getProductDetail, packagingUnitOrDefault} from "../../../api/products-api";
import {Param, readUrlParameter, writeToUrl} from "../../../utils/url.utils";
import {
  createHtmlElementFromString,
  createPatientGroupIconsFromString,
  createUseIconsFromId
} from "../../../utils/html.utils";
import {getPriceWithCurrencyForOnePiece} from "../../../api/shopping-cart-api";

/**
 * Initializes the e-catalog tile and sets listeners
 */
class ECatalogTileModule implements ComponentObservable {
  /**
   * Selector of all buttons that must be initialized in javascript.
   */
  componentSelector = '.tile--e-catalog';

  /**
   * Selector of the 'new wish list' overlay.
   */
  addToWishlistOverlaySelector = `#${selectWishListOverlay.overlayId}`;

  userId = '';
  translations = null;

  /**
   * One row in the product details table.
   */
  productDetailsRow = `
  <tr>
    <th></th>
    <td></td>
  </tr>`;

  /**
   * Initialize all e-catalog tiles on the current page.
   * @param observe Boolean if the ECatalogTileModule should listen for changes in the DOM and initialize dynamically added
   *   buttons
   */
  initializeAll(observe: boolean): void {
    const allElements = document.querySelectorAll(this.componentSelector);
    this.userId = document.querySelector('html').dataset.uid;
    for (let i = 0; i < allElements.length; i++) {
      this.initialize(allElements[i] as HTMLElement);
    }

    if (observe) {
      this.startListening();
    }
  }

  /**
   * Listen for changes in DOM and initialize e-catalog tiles when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  outsideClickListener(event: MouseEvent, tooltip: HTMLElement): void {
    const target = event.target as HTMLElement;
    if (target !== tooltip && !tooltip.contains(target) && tooltip.parentElement !== target) {
      tooltip.setAttribute('aria-hidden', 'false');
      // Timeout for animation
      setTimeout(() => {
        tooltip.setAttribute('aria-hidden', 'true');
      }, 200);
    } else {
      // If tooltip is not closed, add new window listener that listens only once to the click event
      window.addEventListener('click', (event) => this.outsideClickListener(event, tooltip), {
        once: true
      });
    }
  }

  /**
   * Initialize the given e-catalog tile.
   * @param root The tile root element that should be initialized
   */
  initialize(root: HTMLElement): void {
    const ecatalogModule = document.querySelector<HTMLElement>(('.e-catalog'));
    this.translations = JSON.parse(ecatalogModule.dataset.translations)
    componentObserver.markElementAsInitialized(this.componentSelector, root);

    // Set tooltip click listener
    const discountButton = root.querySelector<HTMLElement>('.discount');
    const discountTooltip = root.querySelector<HTMLElement>('.discount-tooltip');
    if (discountButton && discountTooltip) {
      discountButton.addEventListener(
        'touchstart',
        (event: KeyboardEvent) => {
          event.stopPropagation();
          event.preventDefault();

          // Check if tooltip is not opened yet
          if (discountTooltip.getAttribute('aria-hidden') === 'true') {
            // Show tooltip
            discountTooltip.setAttribute('aria-hidden', 'false');
            // Timeout for animation
            setTimeout(() => {
              discountTooltip.removeAttribute('aria-hidden');
            }, 50);
            // Add global window click listener that is fired once
            window.addEventListener('click', (event) => this.outsideClickListener(event, discountTooltip), {
              once: true
            });
          } else {
            discountTooltip.setAttribute('aria-hidden', 'false');
            // Timeout for animation
            setTimeout(() => {
              discountTooltip.setAttribute('aria-hidden', 'true');
            }, 200);
          }
        },
        false
      );

      // also show the tooltip on hover on desktop
      discountButton.addEventListener(
        'mouseenter',
        (event: MouseEvent) => {
          event.stopPropagation();
          event.preventDefault();
          // Check if tooltip is not opened yet
          if (discountTooltip.getAttribute('aria-hidden') === 'true') {
            // Show tooltip
            discountTooltip.setAttribute('aria-hidden', 'false');
            // Timeout for animation
            setTimeout(() => {
              discountTooltip.removeAttribute('aria-hidden');
            }, 50);

            // Hide the tooltip when the mouse leaves the tooltip button
            discountButton.addEventListener(
              'mouseleave',
              () => {
                discountTooltip.setAttribute('aria-hidden', 'false');
                // Timeout for animation
                setTimeout(() => {
                  discountTooltip.setAttribute('aria-hidden', 'true');
                }, 200);
              },
              {
                once: true
              }
            );
          }
        },
        false
      );
    }

    // set click listener for the 'add to wish list' button
    const pinButton = root.querySelector<HTMLElement>('.tile__pin-button');
    const productId = root.dataset.productId;
    //const quickInfoButton = root.querySelector<HTMLElement>('.button-quick-info');
    //this.addListenerForQuickInfoButton(quickInfoButton, productId);
    this.addListenerForPinButton(pinButton, productId);
  }

  addListenerForQuickInfoButton(quickInfoButton: HTMLElement, productId: string) {
    if (quickInfoButton) {
      // Default wishlist behavior
      quickInfoButton.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        getProductDetail(productId).then((product) => {
          document.getElementById('ecatalog-quick-info-overlay').classList.add('overlay--visible');
          const table = document.getElementById('ecatalog-quick-info-overlay').querySelector<HTMLTableElement>('.product-detail-table');
          const headline = document.getElementById('ecatalog-quick-info-overlay').querySelector<HTMLTableElement>('.product-detail__headline');
          headline.innerText = product.name;
          table.innerHTML = '';
          let cellIndex = 0;

          if (product.typeOfUse) {
            const icon = createUseIconsFromId(product.typeOfUse, this.translations);
            this.addTableRow(table, cellIndex, this.translations.typeOfUse, icon);
            cellIndex++;
          }
          if (product.patientGroups) {
            const icon = createPatientGroupIconsFromString(product.patientGroups, this.translations);
            this.addTableRow(table, cellIndex, this.translations.patientGroups, icon);
            cellIndex++;
          }
          const packingUnit = createHtmlElementFromString(
              `<div class="packing-unit" title="${this.translations.packingUnit}">${packagingUnitOrDefault(product)}</div>`
          );
          this.addTableRow(table, cellIndex, this.translations.packingUnit, packingUnit);
          cellIndex++;
          if (product.forVentilator) {
            this.addTableRow(table, cellIndex, this.translations.forVentilator, product.forVentilator);
            cellIndex++;
          }
          if (product.material) {
            this.addTableRow(table, cellIndex, this.translations.material, product.material);
            cellIndex++;
          }
          if (product.note) {
            const noteElement = document.createElement('div');
            noteElement.innerHTML = product.note.replaceAll('\n', '');
            this.addTableRow(table, cellIndex, this.translations.noteLabel, noteElement);
            cellIndex++;
          }
          if (product.gtin) {
            this.addTableRow(table, cellIndex, this.translations.gtin, product.gtin);
            cellIndex++;
          }
          if (product.height || product.length || product.width) {
            let sizes = [];
            let labels = [];
            let sizeValue = '';
            let sizeLabel = this.translations.dimensionsUnit;

            if (product.width) {
              sizes.push(product.width);

              if (this.translations.dimensionWidth) {
                labels.push(this.translations.dimensionWidth);
              }
            }
            if (product.length) {
              sizes.push(product.length);

              if (this.translations.dimensionLength) {
                labels.push(this.translations.dimensionLength);
              }
            }
            if (product.height) {
              sizes.push(product.height);

              if (this.translations.dimensionHeight) {
                labels.push(this.translations.dimensionHeight);
              }
            }

            sizeValue = sizes.join('x');
            if (sizeLabel.length > 0 && this.translations.dimensionUnitLabel) {
              sizeLabel = '(' + labels.join('x') + ' ' + (this.translations.dimensionUnitLabel || '') + ')';
              this.addTableRow(
                  table,
                  cellIndex,
                  this.translations.dimensions,
                  `${sizeValue} ${sizeLabel}`
              );
            } else {
              this.addTableRow(
                  table,
                  cellIndex,
                  this.translations.dimensions,
                  `${product.width||0}x${product.length||0}x${product.height||0} ${this.translations.dimensionsUnit}`
              );
            }

            cellIndex++;
          }
          if (product.weight) {
            this.addTableRow(table, cellIndex, this.translations.weight, `${product.weight} kg`);
            cellIndex++;
          }
          if (product.packagingHeight && product.packagingLength && product.packagingWidth) {
            this.addTableRow(
                table,
                cellIndex,
                this.translations.packagingDimensions,
                `${product.packagingWidth}x${product.packagingLength}x${product.packagingHeight} ${this.translations.packagingDimensionsUnit}`
            );
            cellIndex++;
          }
          if (product.packagingWeight) {
            this.addTableRow(table, cellIndex, this.translations.packagingWeight, `${product.packagingWeight} kg`);
            cellIndex++;
          }
          if (product.shelfLife) {
            this.addTableRow(table, cellIndex, this.translations.shelfLife, `${product.shelfLife}`);
            cellIndex++;
          }

          if (product.includedArticles) {
            const includedArticlesElem = document.createElement('span');
            for (const article of product.includedArticles) {
              const articleLink = document.createElement('a');
              articleLink.innerText = `${this.translations.pn} ${article.partNumber} `;
              articleLink.id = 'product_' + article['@id'];
              includedArticlesElem.appendChild(articleLink);
              includedArticlesElem.appendChild(document.createTextNode(article.name));
              includedArticlesElem.appendChild(document.createElement('br'));
            }

            this.addTableRow(table, cellIndex, this.translations.includedArticles, includedArticlesElem);
            cellIndex++;

            setTimeout(() => {
              for (const article of product.includedArticles) {
                document.querySelector('#product_' + article['@id']).addEventListener('click', () => {
                  writeToUrl({ param: Param.PRODUCT, value: article['@id'] }, [Param.TAB, Param.CATEGORY]);
                });
              }
            }, 10);
          }

          if (product.requiredArticles) {
            const requiredArticlesElem = document.createElement('span');
            for (const article of product.requiredArticles) {
              const articleLink = document.createElement('a');
              articleLink.innerText = `${this.translations.pn} ${article.partNumber} `;
              articleLink.id = 'product_' + article['@id'];
              requiredArticlesElem.appendChild(articleLink);
              requiredArticlesElem.appendChild(document.createTextNode(article.name));
              requiredArticlesElem.appendChild(document.createElement('br'));
            }

            this.addTableRow(table, cellIndex, this.translations.requiredArticles, requiredArticlesElem);
            cellIndex++;

            setTimeout(() => {
              for (const article of product.includedArticles) {
                document.querySelector('#product_' + article['@id']).addEventListener('click', () => {
                  writeToUrl({ param: Param.PRODUCT, value: article['@id'] }, [Param.TAB, Param.CATEGORY]);
                });
              }
            }, 10);
          }

          if (product.price) {
            this.addTableRow(
                table,
                cellIndex,
                this.translations.priceExclVat,
                getPriceWithCurrencyForOnePiece(product.price)
            );
          }
        }).catch((err) => {
          console.log(err);
        })
      });
    }
  }

  /**
   * Adds a row to the product detail table.
   * @param table The table to add a row to
   * @param index The current index
   * @param label The label for the th element
   * @param value The value for the td element
   */
  addTableRow(table: HTMLTableElement, index: number, label: string, value: HTMLElement | string): void {
    const tableRow = table.insertRow(index);
    tableRow.innerHTML = this.productDetailsRow;
    tableRow.querySelector('th').innerText = label;
    if (value instanceof HTMLElement) {
      tableRow.querySelector('td').appendChild(value);
    } else {
      tableRow.querySelector('td').innerText = value;
    }
  }

  /**
   * Listen for clicks on the pin button and open the 'add to wish list' dialog.
   */
  addListenerForPinButton(pinButton: HTMLElement, productId: string): void {
    if (pinButton) {
      // Default wishlist behavior
      pinButton.addEventListener('click', async (e) => {
        if (window.localStorage) {
          const cachedWishlistItems = await wishListData.getCachedWishListProductIds(false);
          if (cachedWishlistItems.indexOf(productId) > -1) {
            // Product already added to a wishlist. Go with default behaviour and let user select to remove or add to another list
            return;
          }
          const defaultWishlist = window.localStorage.getItem('defaultWishlist' + this.userId);
          if (defaultWishlist) {
            e.stopImmediatePropagation();
            e.preventDefault();
            
            getAllWishlists()
            .then((wishLists) => {
              const defaultWishlistObject = wishLists.filter((wishlist) => wishlist['@name'] === defaultWishlist)[0];
              if (!defaultWishlistObject.items) defaultWishlistObject.items = [];
              const defaultWishlistItems = defaultWishlistObject.items.filter((item) => item['@id'] === productId);
              const currentItemIds = defaultWishlistObject.items?.map((item) => item['@id']) || [];
              const overlay = document.querySelector<HTMLElement>('.overlay-wrapper[data-label-wishlist-added]');
              const labelWishlistAdded = overlay.dataset.labelWishlistAdded;
              const labelWishlistRemoved = overlay.dataset.labelWishlistRemoved;
              const errorMessage = overlay.dataset.errorMessage;
              const labelWishlistGoTo = overlay.dataset.labelWishlistGoTo;
              let added = false;

              if (defaultWishlistItems.length === 0) {
                added = true;
                addItemToWishList(productId, currentItemIds, defaultWishlist)
                  .then(() => {
                    wishListData.productIdsInWishLists.push(productId);
                    eCatalog.eCatalogProducts.updatePinButtonStates();
                    //wishList.createWishListItem()
                  })
                  .catch(err => {
                    console.log(err);
                    toastNotification.createErrorToastMessage(errorMessage)
                  });
              } else {
                deleteItemFromWishList(productId, currentItemIds, defaultWishlist)
                  .then(() => {
                    wishListData.removeProductFromCachedWishList(productId);
                    eCatalog.eCatalogProducts.updatePinButtonStates();
                  })  
                  .catch(err => toastNotification.createErrorToastMessage(errorMessage));
              }

              const labelText = added ? labelWishlistAdded : labelWishlistRemoved;
              const breakpoint = currentBreakpoint();
              let product = document.querySelector(`[data-product-id="${productId}"]`);
              const fallbackIcons = product
              ? product.getAttribute('data-fallback-icons')
              : document
                  .querySelector<HTMLImageElement>('.text-and-image-component__image-wrapper')
                  ?.getAttribute('data-fallback-icons');
              const productName = product
                ? product.querySelector<HTMLElement>('.tile__headline').innerText
                : document.querySelector<HTMLElement>('.product-detail__headline').innerText;
              const partNumber = product
                ? product.querySelector<HTMLElement>('.tile__subtitle').innerText
                : document.querySelector<HTMLElement>('.product-detail__id').innerText;
              const imageSmall = product
                ? product.querySelector<HTMLImageElement>('img')?.src
                : document.querySelector<HTMLImageElement>('.text-and-image-component__image-wrapper img')?.src;

              const fallbackIconArray = fallbackIcons ? JSON.parse(fallbackIcons) : null;
              const productInfo = { productName, partNumber, imageSmall, nodeid: productId, icons: fallbackIconArray };
              if (breakpoint.isMobilePortrait || breakpoint.isMobileLandscape) {
                // on mobile show a toast
                toastNotification.createMobileToast(productInfo, labelText, labelWishlistGoTo, () => {
                  wishList.openWishList();
                });
              } else {
                // Show toastify toast on desktop
                toastNotification.createDesktopToast(productInfo, labelText);
              }
            });
          }
        }
      });

      // also store the ID of the product, that should be added to a wish list
      // this ID will be set to the overlay and later on used in the API call
      pinButton.setAttribute('data-overlay-associated-id', productId);
    }
  }
}

const eCatalogTileModule = new ECatalogTileModule();
export { eCatalogTileModule as eCatalogTile };
